import { useEffect, useState } from 'react'
import { API_BASE } from './api'

/* ================================================================== */
/*  Public LifeCrafters feed (no-auth), for the community "book a       */
/*  consultation" lanes. Crafters are grouped into their admin-assigned  */
/*  tier (premier / elite / base) and filtered to the community they      */
/*  belong to. Falls back to static content when the feed is empty or     */
/*  unreachable — same graceful pattern as the events feed.               */
/* ================================================================== */

export type LifecrafterTier = 'premium' | 'elite' | 'mentor'

// Order shown in the UI (most prestigious first) + display labels. Matches the
// backend enum (profile.model.ts: mentor | elite | premium).
const TIER_ORDER: LifecrafterTier[] = ['premium', 'elite', 'mentor']
const TIER_LABEL: Record<LifecrafterTier, string> = { premium: 'Premium', elite: 'Elite', mentor: 'Mentor' }
const MAX_PER_LANE = 4

type RawLifecrafter = {
  _id: string
  username?: string
  about_me?: string
  profile_pic_url?: string
  lifecrafter_tier?: string
  address?: { city?: string; state?: string }
  event_1on1_config?: { price_per_minute?: number }
  user?: { name?: string; _id?: string }
  categories?: { name?: string }[]
}

export type Lifecrafter = {
  id: string
  name: string
  line: string
  photo?: string
  tier: LifecrafterTier
  pricePerMinute?: number
  city?: string
}

/** One tier's worth of crafters, ready to drop into a consultation lane. */
export type LifecrafterLane = {
  tier: LifecrafterTier
  label: string // "Premier"
  startsAt?: string // "₹10/min"
  people: Lifecrafter[]
}

async function getPublic<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`)
  if (!res.ok) throw new Error(`lifecrafters request failed (${res.status})`)
  return res.json() as Promise<T>
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function normaliseTier(t?: string): LifecrafterTier {
  const v = (t || '').toLowerCase()
  if (v === 'premium' || v === 'premier') return 'premium'
  if (v === 'elite') return 'elite'
  return 'mentor'
}

function mapLifecrafter(lc: RawLifecrafter): Lifecrafter {
  const name = lc.user?.name?.trim() || lc.username?.trim() || 'LifeCrafter'
  const city = lc.address?.city?.trim() || undefined
  const about = (lc.about_me || '').replace(/[*_#`]/g, '').trim()
  // First sentence of the bio, else a location line, else a neutral default.
  // (Avoid a lookbehind regex here — it throws on Safari < 16.4 and older browsers.)
  const line = about ? (about.match(/^[^.]*\./)?.[0] ?? about) : city ? `Based in ${city}` : '1-on-1 sessions'
  return {
    id: lc._id,
    name,
    line,
    photo: lc.profile_pic_url || undefined,
    tier: normaliseTier(lc.lifecrafter_tier),
    pricePerMinute: lc.event_1on1_config?.price_per_minute,
    city,
  }
}

function groupByTier(people: Lifecrafter[]): LifecrafterLane[] {
  return TIER_ORDER.map((tier) => {
    const inTier = people.filter((p) => p.tier === tier)
    const prices = inTier.map((p) => p.pricePerMinute).filter((n): n is number => typeof n === 'number' && n > 0)
    const min = prices.length ? Math.min(...prices) : undefined
    return {
      tier,
      label: TIER_LABEL[tier],
      startsAt: min != null ? `₹${min}/min` : undefined,
      people: inTier.slice(0, MAX_PER_LANE),
    }
  }).filter((lane) => lane.people.length > 0)
}

/** Bookable crafters for one community, grouped into tier lanes. */
export async function fetchCommunityLifecrafters(communityName: string): Promise<LifecrafterLane[]> {
  const start = new Date()
  const end = new Date(start.getTime() + 28 * 24 * 60 * 60 * 1000) // ≤31-day window the API allows
  const qs = `startDate=${ymd(start)}&endDate=${ymd(end)}&durationMins=30`
  const data = await getPublic<{ results?: RawLifecrafter[] }>(`/events/public/lifecrafters/available?${qs}`)
  const want = communityName.trim().toUpperCase()
  const mine = (data.results ?? []).filter((lc) =>
    (lc.categories ?? []).some((c) => (c.name ?? '').trim().toUpperCase() === want),
  )
  return groupByTier(mine.map(mapLifecrafter))
}

/** null while loading (keep static), [] when none. */
export function useCommunityLifecrafters(communityName: string | undefined): LifecrafterLane[] | null {
  const [lanes, setLanes] = useState<LifecrafterLane[] | null>(null)
  useEffect(() => {
    if (!communityName) {
      setLanes([])
      return
    }
    let alive = true
    setLanes(null)
    fetchCommunityLifecrafters(communityName)
      .then((r) => alive && setLanes(r))
      .catch(() => alive && setLanes([]))
    return () => {
      alive = false
    }
  }, [communityName])
  return lanes
}

/* ------------------------------------------------------------------ */
/*  Name → booking-id index. Lets a curated/hardcoded crafter's "Book"   */
/*  button deep-link to their specific webapp 1-1 page when that crafter  */
/*  exists on the webapp; unmatched crafters fall back to the generic     */
/*  community booking. Best-effort loose name match.                      */
/* ------------------------------------------------------------------ */

/** Strip honorifics/punctuation for loose name matching. */
function normName(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/\b(acharya|dr|pt|pandit|ustad|ustaad|shri|smt|mr|mrs|ms)\b\.?/g, '')
    .replace(/ji\b/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Fetch the live "available" feed once and index each crafter by normalised
 *  full name and first name → their booking id. Empty until loaded. */
export function useLifecrafterIndex(): Record<string, string> {
  const [idx, setIdx] = useState<Record<string, string>>({})
  useEffect(() => {
    let alive = true
    const start = new Date()
    const end = new Date(start.getTime() + 28 * 24 * 60 * 60 * 1000)
    getPublic<{ results?: RawLifecrafter[] }>(
      `/events/public/lifecrafters/available?startDate=${ymd(start)}&endDate=${ymd(end)}&durationMins=30`,
    )
      .then((d) => {
        if (!alive) return
        const map: Record<string, string> = {}
        for (const lc of d.results ?? []) {
          const name = lc.user?.name?.trim()
          const bookingId = lc.user?._id // the id the 1-1 booking page expects
          if (!name || !bookingId) continue
          const n = normName(name)
          if (!n) continue
          map[n] = bookingId
          const first = n.split(' ')[0]
          if (first && !(first in map)) map[first] = bookingId
        }
        setIdx(map)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  return idx
}

/* Static fallback: normalised crafter name → webapp 1-1 booking id (user._id),
   captured from the public availability feed. The live feed (useLifecrafterIndex)
   is preferred, but it is CORS-blocked in the browser in prod, so without this
   every "Book" CTA would drop to the generic all-crafters page instead of the
   specific crafter. Refresh from GET /events/public/lifecrafters/available. */
const STATIC_LIFECRAFTER_IDS: Record<string, string> = {
  'aindrila': '6aa988b6e92e8ae8af295b70', 'aindrila sarkar': '6aa988b6e92e8ae8af295b70',
  'akarshita': '6aa129b159e487bf29d779e7', 'akarshita srivastava': '6aa129b159e487bf29d779e7',
  'amita': '6aa5509c0ebb0e3a1da9077a', 'amita srivastava': '6aa5509c0ebb0e3a1da9077a',
  'arun': '6aa38d1a0597cf21ce7f0581', 'arun bansal': '6aa38d1a0597cf21ce7f0581',
  'asha': '6aa38d190597cf21ce7f04c1', 'asha tyagi': '6aa38d190597cf21ce7f04c1',
  'bhaskar': '6aa5509c0ebb0e3a1da9074c', 'bhaskar yogacharya': '6aa5509c0ebb0e3a1da9074c',
  'biswadeep': '6aa129b259e487bf29d77a47',
  'deepak': '6a9c1612cd419407bd787215', 'deepak nainwal': '6a9c1612cd419407bd787215', 'deepak gupta': '6aa38d180597cf21ce7f04a9',
  'emmanuel': '6aa3b20d0597cf21ce7f1535', 'emmanuel samson egbert': '6aa3b20d0597cf21ce7f1535',
  'ghatti': '6aa7dcefbff61b26c75132af', 'ghatti veda bhaskaram': '6aa7dcefbff61b26c75132af',
  'hardeep': '6aa38d190597cf21ce7f04d9', 'hardeep sidana': '6aa38d190597cf21ce7f04d9',
  'hira': '6aa129b159e487bf29d779ff', 'hira gain': '6aa129b159e487bf29d779ff',
  'indrani': '6a9e80a98f7256ca21459114',
  'manjuu': '6aa38d1a0597cf21ce7f0599', 'manjuu sharma': '6aa38d1a0597cf21ce7f0599',
  'minaakshi': '6aa38d1a0597cf21ce7f0569', 'minaakshi rathour': '6aa38d1a0597cf21ce7f0569',
  'naresh': '6aa38d190597cf21ce7f0509', 'naresh kumar pathak': '6aa5509d0ebb0e3a1da90791', 'naresh pathak': '6aa5509d0ebb0e3a1da90791',
  'nayan': '6aaa34b9e92e8ae8af2983f1', 'nayan tomar': '6aaa34b9e92e8ae8af2983f1',
  'nidhi': '6a9e80a88f7256ca2145909c',
  'nookala': '6aa38d190597cf21ce7f0521', 'nookala suryanarayana': '6aa38d190597cf21ce7f0521',
  'pawan': '6a9c18c6dbccdb3df8f4dfee',
  'poonam': '6aa6983ad998a196c334667d', 'poonam gaur': '6aa6983ad998a196c334667d',
  'puneet': '6aacf0ec6ce5dc010d9e51a6', 'puneet garg': '6aacf0ec6ce5dc010d9e51a6',
  'raghvendra': '6aa7c6d4bb161a0c0b528b9b',
  'sayan': '6aa988b5e92e8ae8af295b59', 'sayan bose': '6aa988b5e92e8ae8af295b59',
  'shubhniti': '6a9e80a98f7256ca214590cc',
  'sukanta': '6aa7dc7dbff61b26c7512b12',
  'vidhi': '6aa7c6d4bb161a0c0b528b82', 'vidhi varshney': '6aa7c6d4bb161a0c0b528b82',
  'vijay': '6aa38d180597cf21ce7f0491', 'vijay kumar': '6aa38d180597cf21ce7f0491',
  'vikas': '6a9c1a2edbccdb3df8f4e0a2', 'vikas kumar': '6a9c1a2edbccdb3df8f4e0a2',
  'vivek': '6aa38d1a0597cf21ce7f0539', 'vivek kush': '6aa38d1a0597cf21ce7f0539',
}

/** Look up a curated crafter's booking id by name (full match, else first name),
 *  preferring the live feed and falling back to the static map above. */
export function lookupLifecrafterId(idx: Record<string, string>, name: string): string | undefined {
  const n = normName(name)
  if (!n) return undefined
  const first = n.split(' ')[0]
  return idx[n] ?? idx[first] ?? STATIC_LIFECRAFTER_IDS[n] ?? STATIC_LIFECRAFTER_IDS[first]
}
